/**
 * LtrToken — inline wrapper that pins a numeric / code token to a left-to-right
 * base direction and isolates it from the surrounding text (a `<bdi dir="ltr">`).
 *
 * In an RTL document (Hebrew, Arabic) the Unicode Bidirectional Algorithm
 * reorders runs that mix strong-LTR characters (digits, latin) with weak
 * characters (spaces, slashes, dots, commas, currency symbols). That visually
 * scrambles tokens that must read strictly left-to-right:
 *   - money amounts ("1,234.50 USD")
 *   - dates ("31/12/2026")
 *   - quantities + units ("12.5 kg")
 *   - SKUs / reference / ID codes ("PO-2026-00042")
 *   - phone numbers ("+1 415 555 0123")
 * Wrapping such a token in `<bdi dir="ltr">` forces its internal order to LTR
 * and shields it from the document direction, so it always reads correctly
 * regardless of locale. The element stays inline, so it never reorders the
 * surrounding flex/layout — only the token's own glyph order.
 *
 * This is the class-B counterpart to {@link BidiText} (class A — display labels
 * whose language is unknown at author time, which BidiText resolves by word
 * majority rather than by the browser's `dir="auto"` first-strong rule; see
 * STYLE_GUIDE § Bidirectional Text and WI cf3fc9a4). Pick LtrToken for
 * digit+separator tokens; pick BidiText for human-language names/labels; for our
 * own translated copy pick neither and let it inherit the document direction.
 *
 * Usage:
 *   <LtrToken>{formattedAmount}</LtrToken>
 *
 * Most call sites get this for free: the shared localized display components
 * (LocalizedCurrencyDisplay, LocalizedNumberDisplay, LocalizedDateDisplay,
 * LocalizedDateTimeDisplay, MeasurementDisplay, DimensionsDisplay) already
 * render their formatted output through LtrToken. Reach for it directly only
 * when you render a raw code/SKU/reference string outside those components.
 */

import React from 'react';

/**
 * The string twin of {@link LtrToken}, for a token that must go INSIDE a
 * translated sentence rather than beside one.
 *
 * `<bdi dir="ltr">` cannot be used there: the sentence is a single `t()` string,
 * and splitting it into "prefix + token + suffix" so the token can be an element
 * would hardcode the word order in JSX — which is precisely what a translator
 * needs to control (Hebrew and Chinese put the verb before the count where
 * English puts it after). U+2068 FIRST STRONG ISOLATE + U+2069 POP DIRECTIONAL
 * ISOLATE are the Unicode characters that express the same isolation as `<bdi>`,
 * so the token survives interpolation with its internal order intact.
 *
 * Reach for this ONLY for a token that is interpolated into a translated string.
 * Anything rendered as its own element still uses `<LtrToken>` — an element is
 * clearer, inspectable, and cannot be lost to a string operation.
 *
 * `⁨` is FIRST STRONG rather than LTR ISOLATE (`⁦`) deliberately: a
 * digit run resolves LTR either way, and first-strong keeps the helper correct
 * for a token whose own content might legitimately be RTL.
 */
export function ltrIsolated(token: string): string {
  return `⁨${token}⁩`;
}

type LtrTokenProps = React.HTMLAttributes<HTMLElement> & {
  // Optional so the token can serve as a `<Trans components={{ n: <LtrToken /> }}>`
  // placeholder — react-i18next injects the children via cloneElement at runtime.
  children?: React.ReactNode;
};

// `forwardRef` so the token can be used directly as a Radix `asChild` Slot
// child (e.g. inside a `<TooltipTrigger asChild>`, which the Localized*
// display components do when `showTooltip` is set) without React warning that
// a function component cannot receive a ref.
export const LtrToken = React.forwardRef<HTMLElement, LtrTokenProps>(
  function LtrToken({ children, ...props }, ref) {
    // `<bdi>` is a bidi isolate by default (UA stylesheet sets
    // `unicode-bidi: isolate`); `dir="ltr"` additionally forces the run's base
    // direction so digit+separator tokens never flip in an RTL context.
    return (
      <bdi dir="ltr" ref={ref} {...props}>
        {children}
      </bdi>
    );
  }
);
